import { useMemo, useState } from "react";

import { Badge, Button, Card, Heading, Loader, Notification, Select } from "@stellar/design-system";

import { ErrorWithExtras } from "@/components/ErrorWithExtras";
import { Pagination } from "@/components/Pagination";
import { SectionHeader } from "@/components/SectionHeader";

import { useMarkAllNotificationsRead } from "@/apiQueries/useMarkAllNotificationsRead";
import { useMarkNotificationRead } from "@/apiQueries/useMarkNotificationRead";
import { useNotifications } from "@/apiQueries/useNotifications";
import { useNotificationsUnreadCount } from "@/apiQueries/useNotificationsUnreadCount";
import { PAGE_LIMIT_OPTIONS } from "@/constants/settings";
import { number } from "@/helpers/formatIntlNumber";
import {
  ApiNotification,
  ApiNotificationMetadata,
  NotificationEventType,
  NotificationReadStatus,
  NotificationSignerContext,
} from "@/types";

import "./Notifications.scss";

const STATUS_OPTIONS: { value: NotificationReadStatus; label: string }[] = [
  { value: "unread", label: "Unread" },
  { value: "all", label: "All" },
  { value: "read", label: "Read" },
];

const EVENT_BADGE_VARIANTS: Record<NotificationEventType, "success" | "error" | "warning" | "tertiary" | "secondary"> = {
  SIGNER_INVITATION: "secondary",
  SIGNATURE_REQUESTED: "warning",
  SIGNATURE_COLLECTED: "tertiary",
  TRANSACTION_SUCCESS: "success",
  TRANSACTION_FAILURE: "error",
};

const EVENT_LABELS: Record<NotificationEventType, string> = {
  SIGNER_INVITATION: "Signer Invitation",
  SIGNATURE_REQUESTED: "Signature Requested",
  SIGNATURE_COLLECTED: "Signature Progress",
  TRANSACTION_SUCCESS: "Transaction Submitted",
  TRANSACTION_FAILURE: "Transaction Failed",
};

const toMetadata = (notification: ApiNotification): ApiNotificationMetadata => {
  if (!notification.metadata || typeof notification.metadata !== "object") {
    return {};
  }

  return notification.metadata;
};

const formatPublicKey = (value?: string) => {
  if (!value) {
    return null;
  }

  if (value.length <= 12) {
    return value;
  }

  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const formatAssetAmount = (metadata: ApiNotificationMetadata) => {
  const summary = metadata.disbursement_summary;
  const totalAmount = summary?.total_amount || metadata.total_amount;
  const assetCode = summary?.asset?.code || metadata.asset_code;
  const recipientCount = summary?.recipient_count || metadata.recipient_count;

  if (!totalAmount && !assetCode && !recipientCount) {
    return null;
  }

  const amountLabel = totalAmount && assetCode ? `${totalAmount} ${assetCode}` : totalAmount || assetCode;
  const recipientsLabel = recipientCount ? `${recipientCount} recipient${recipientCount === 1 ? "" : "s"}` : null;

  return [amountLabel, recipientsLabel].filter(Boolean).join(" • ");
};

const formatEventDetail = (notification: ApiNotification) => {
  const metadata = toMetadata(notification);
  const signerContext = (metadata.signer_context || {}) as NotificationSignerContext;

  switch (notification.event_type) {
    case "SIGNER_INVITATION": {
      const publicKey = formatPublicKey(
        metadata.invited_signer_public_key || signerContext.invited_signer_public_key,
      );

      return publicKey ? `Invited signer: ${publicKey}` : "A new signer needs to connect a wallet.";
    }
    case "SIGNATURE_REQUESTED": {
      const amountSummary = formatAssetAmount(metadata);
      const requiredSignatures = metadata.required_signature_count;

      if (amountSummary && requiredSignatures) {
        return `${amountSummary} • ${requiredSignatures} signatures required`;
      }

      if (amountSummary) {
        return amountSummary;
      }

      return "A multisig disbursement is waiting for signatures.";
    }
    case "SIGNATURE_COLLECTED": {
      const collected = signerContext.collected_signature_count || metadata.collected_signature_count;
      const required = signerContext.required_signature_count || metadata.required_signature_count;
      const publicKey = formatPublicKey(
        signerContext.collected_signer_public_key || metadata.collected_signer_public_key,
      );

      const progress = collected && required ? `${collected} of ${required} signatures collected` : null;
      const signer = publicKey ? `Latest signer: ${publicKey}` : null;
      return [progress, signer].filter(Boolean).join(" • ") || "Signature collection is in progress.";
    }
    case "TRANSACTION_SUCCESS": {
      const txHash = metadata.tx_hash;
      const amountSummary = formatAssetAmount(metadata);
      return [amountSummary, txHash ? `Tx: ${txHash}` : null]
        .filter(Boolean)
        .join(" • ") || "The multisig transaction was submitted successfully.";
    }
    case "TRANSACTION_FAILURE": {
      const reason = metadata.failure_reason;
      const txHash = metadata.tx_hash;
      return [reason ? `Reason: ${reason}` : null, txHash ? `Tx: ${txHash}` : null]
        .filter(Boolean)
        .join(" • ") || "The multisig transaction failed before completion.";
    }
    default:
      return null;
  }
};

const formatTimestamp = (value: string) => {
  return new Date(value).toLocaleString();
};

export const Notifications = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(20);
  const [statusFilter, setStatusFilter] = useState<NotificationReadStatus>("unread");

  const searchParams = useMemo(
    () => ({
      page: currentPage.toString(),
      page_limit: pageLimit.toString(),
      status: statusFilter,
    }),
    [currentPage, pageLimit, statusFilter],
  );

  const { data, error, isLoading, isFetching } = useNotifications(searchParams);
  const { unreadCount } = useNotificationsUnreadCount();
  const markNotificationRead = useMarkNotificationRead();
  const markAllNotificationsRead = useMarkAllNotificationsRead();

  const notifications = data?.data || [];
  const total = data?.pagination?.total || 0;
  const maxPages = data?.pagination?.pages || 1;
  const isBusy = isLoading || isFetching;
  const showMarkAll = unreadCount > 0 && statusFilter !== "read";
  const mutationError = markNotificationRead.error || markAllNotificationsRead.error;

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentPage(1);
    setStatusFilter(event.target.value as NotificationReadStatus);
  };

  const handlePageLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentPage(1);
    setPageLimit(Number(event.target.value));
  };

  const handleMarkRead = async (notificationId: string) => {
    await markNotificationRead.mutateAsync(notificationId);
  };

  const handleMarkAll = async () => {
    await markAllNotificationsRead.mutateAsync();
  };

  return (
    <div className="NotificationsPage">
      <SectionHeader>
        <SectionHeader.Row>
          <SectionHeader.Content>
            <div className="NotificationsPage__titleRow">
              <Heading as="h2" size="sm">
                {total > 0 ? `${number.format(total)} ` : ""}
                Notifications
              </Heading>
              {unreadCount > 0 ? <Badge variant="warning">{`${unreadCount} unread`}</Badge> : null}
            </div>
          </SectionHeader.Content>
          <SectionHeader.Content align="right">
            {showMarkAll ? (
              <Button
                variant="tertiary"
                size="md"
                onClick={handleMarkAll}
                disabled={markAllNotificationsRead.isPending || unreadCount === 0}
              >
                {markAllNotificationsRead.isPending ? "Marking..." : "Mark all as read"}
              </Button>
            ) : null}
          </SectionHeader.Content>
        </SectionHeader.Row>

        <SectionHeader.Row>
          <SectionHeader.Content>
            <div className="NotificationsPage__controls">
              <Select
                id="notification-status"
                fieldSize="md"
                label="Status"
                value={statusFilter}
                onChange={handleStatusChange}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </SectionHeader.Content>
          <SectionHeader.Content align="right">
            <div className="NotificationsPage__controls NotificationsPage__controls--right">
              <Select
                id="notifications-page-limit"
                fieldSize="md"
                value={pageLimit}
                onChange={handlePageLimitChange}
              >
                {PAGE_LIMIT_OPTIONS.map((pageSize) => (
                  <option key={pageSize} value={pageSize}>{`Show ${pageSize} results`}</option>
                ))}
              </Select>

              <Pagination
                currentPage={currentPage}
                maxPages={maxPages}
                onSetPage={setCurrentPage}
                isLoading={isBusy}
              />
            </div>
          </SectionHeader.Content>
        </SectionHeader.Row>
      </SectionHeader>

      {error ? <ErrorWithExtras appError={{ message: error.message, extras: error.extras }} /> : null}
      {mutationError ? (
        <Notification variant="error" title="Could not update notifications" isFilled={true}>
          <ErrorWithExtras appError={{ message: mutationError.message, extras: mutationError.extras }} />
        </Notification>
      ) : null}

      {isBusy && !data ? (
        <div className="NotificationsPage__loading">
          <Loader />
        </div>
      ) : null}

      {!isBusy && !error && notifications.length === 0 ? (
        <Card>
          <div className="NotificationsPage__emptyState">
            <Heading as="h3" size="xs">
              {statusFilter === "unread" ? "You are all caught up" : "No notifications found"}
            </Heading>
            <p>
              {statusFilter === "unread"
                ? "New multisig signing invitations, signature progress, and transaction outcomes will appear here."
                : "Try another status filter once more notification history is available."}
            </p>
          </div>
        </Card>
      ) : null}

      <div className="NotificationsPage__list">
        {notifications.map((notification) => {
          const detail = formatEventDetail(notification);
          const metadata = toMetadata(notification);

          return (
            <Card key={notification.id}>
              <article
                className={[
                  "NotificationCard",
                  !notification.read_at ? "NotificationCard--unread" : null,
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="NotificationCard__header">
                  <div className="NotificationCard__heading">
                    <div className="NotificationCard__titleRow">
                      {!notification.read_at ? <span className="NotificationCard__unreadDot" /> : null}
                      <Heading as="h3" size="xs">
                        {notification.title}
                      </Heading>
                    </div>

                    <div className="NotificationCard__metaRow">
                      <Badge variant={EVENT_BADGE_VARIANTS[notification.event_type]}>
                        {EVENT_LABELS[notification.event_type]}
                      </Badge>
                      <span>{formatTimestamp(notification.created_at)}</span>
                      {metadata.disbursement_summary?.disbursement_batch_id ? (
                        <span>{`Batch ${metadata.disbursement_summary.disbursement_batch_id}`}</span>
                      ) : null}
                    </div>
                  </div>

                  {!notification.read_at ? (
                    <Button
                      variant="tertiary"
                      size="md"
                      onClick={() => handleMarkRead(notification.id)}
                      disabled={markNotificationRead.isPending}
                    >
                      {markNotificationRead.isPending ? "Saving..." : "Mark as read"}
                    </Button>
                  ) : null}
                </div>

                <p className="NotificationCard__body">{notification.body}</p>
                {detail ? <p className="NotificationCard__detail">{detail}</p> : null}
              </article>
            </Card>
          );
        })}
      </div>
    </div>
  );
};